import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { dashboard } from "@/api";
import { PILLARS, TIER_META, normalizeTier } from "@/constants";
import { useCategories } from "@/hooks/useCategories";
import TierBadge from "@/components/TierBadge";
import { ArrowLeft, Layers } from "lucide-react";

/**
 * Vista filtrada por pilar — pensada para sentarse con un stakeholder específico
 * (ej. equipo de Calidad) y mostrar el ranking de proveedores únicamente para
 * ese pilar, su score promedio y la distribución por nivel.
 */
export default function PillarView() {
  const { pillarKey } = useParams();
  const CATEGORIES = useCategories();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const meta = useMemo(() => PILLARS.find((p) => p.key === pillarKey), [pillarKey]);

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    setErr("");
    dashboard
      .pillar(pillarKey, category ? { category } : {})
      .then((d) => setData(d))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [pillarKey, category, meta]);

  if (!meta) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="font-display text-3xl mb-2">Pilar inválido</div>
        <Link to="/dashboard" className="text-[var(--lala-blue)] underline">Volver al dashboard</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="pillar-view">
      <Link to="/dashboard" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver al dashboard
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">vista por pilar</div>
          <h1 className="font-display text-4xl md:text-5xl" data-testid="pillar-title">{meta.label}</h1>
          <p className="text-[var(--ink-soft)] mt-1">
            Ranking de proveedores por el pilar <strong>{meta.label}</strong>. Útil para revisiones con tu stakeholder especializado.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input-field max-w-[200px]"
            data-testid="pillar-category-filter"
          >
            <option value="">Todas las categorías</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex gap-1.5 flex-wrap" data-testid="pillar-switch">
            {PILLARS.map((p) => (
              <button
                key={p.key}
                onClick={() => nav(`/dashboard/pilar/${p.key}`)}
                className={`sketch-pill px-3 py-1.5 text-xs font-semibold ${
                  p.key === pillarKey ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream)] text-[var(--lala-blue)]"
                }`}
                data-testid={`pillar-switch-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {err && (
        <div className="sketch-border-soft bg-red-50 border-red-200 p-4 mb-6 text-red-700" data-testid="pillar-error">{err}</div>
      )}

      {loading ? (
        <div className="text-center py-20 font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="pillar-stats">
            <Stat label="Promedio del pilar" value={data.average != null ? data.average.toFixed(2) : "—"} sub="escala 0–10" accent="#11142B" />
            <Stat label="Proveedores" value={data.total} sub={`${data.evaluated} con evaluación`} />
            <Stat label="Partner en pilar" value={data.tier_counts?.partner ?? 0} accent={TIER_META.partner.color} />
            <Stat label="Key en pilar" value={data.tier_counts?.key ?? 0} accent={TIER_META.key.color} />
          </div>

          <div className="sketch-border bg-[var(--cream-soft)] overflow-hidden" data-testid="pillar-table">
            <div className="px-4 py-3 bg-[var(--cream-deep)] font-display text-lg flex items-center gap-2">
              <Layers size={18} /> Ranking por {meta.label}
            </div>
            <table className="w-full text-sm">
              <thead className="text-[var(--ink-soft)]">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold w-12">#</th>
                  <th className="px-4 py-2 font-semibold">Proveedor</th>
                  <th className="px-4 py-2 font-semibold">SAP</th>
                  <th className="px-4 py-2 font-semibold">Categoría</th>
                  <th className="px-4 py-2 font-semibold">Score {meta.label}</th>
                  <th className="px-4 py-2 font-semibold">Nivel (pilar)</th>
                  <th className="px-4 py-2 font-semibold">Score global</th>
                  <th className="px-4 py-2 font-semibold">Período</th>
                </tr>
              </thead>
              <tbody>
                {(data.suppliers || []).map((s, idx) => (
                  <tr key={s.id} className="border-t border-[var(--line)] hover:bg-[var(--cream)]" data-testid={`pillar-row-${s.id}`}>
                    <td className="px-4 py-2 font-mono text-[var(--ink-soft)]">{idx + 1}</td>
                    <td className="px-4 py-2 font-semibold">
                      <Link to={`/proveedores/${s.id}`} className="hover:underline text-[var(--lala-blue)]">{s.name}</Link>
                    </td>
                    <td className="px-4 py-2 text-[var(--ink-soft)]">{s.sap_id || "—"}</td>
                    <td className="px-4 py-2 text-[var(--ink-soft)]">{s.category}</td>
                    <td className="px-4 py-2 font-display text-xl" style={{ color: TIER_META[normalizeTier(s.pillar_tier)]?.color }}>
                      {s.pillar_score != null ? s.pillar_score.toFixed(2) : "—"}
                    </td>
                    <td className="px-4 py-2"><TierBadge tier={s.pillar_tier} size="sm" /></td>
                    <td className="px-4 py-2 text-[var(--ink-soft)]">{s.current_score_global != null ? s.current_score_global.toFixed(2) : "—"}</td>
                    <td className="px-4 py-2 text-[var(--ink-soft)]">{s.period || "—"}</td>
                  </tr>
                ))}
                {(data.suppliers || []).length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-10 text-center text-[var(--ink-soft)]">Sin proveedores para mostrar.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub, accent = "var(--lala-blue)" }) {
  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-5">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</div>
      <div className="font-display text-4xl mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-[var(--ink-soft)] mt-1">{sub}</div>}
    </div>
  );
}
